import apiClient from './apiClient'

export interface RoleRef {
  role_id: number
  name: string
  description: string | null
}

export interface UserWithRoles {
  email: string
  // The person's name and Entra object id; null for a row not yet linked.
  display_name?: string | null
  entra_object_id?: string | null
  roles: RoleRef[]
}

// One person the people picker offers, from GET /users/directory.
export interface DirectoryUser {
  id: string
  display_name: string
  user_principal_name: string
  mail: string | null
  job_title: string | null
  department: string | null
  company_name: string | null
  // False for unlicensed accounts and shared mailboxes, shown only on request.
  is_person: boolean
}

export interface DirectoryResponse {
  users: DirectoryUser[]
  // Whether disabled accounts are already left out and is_person means anything.
  full_profile: boolean
}

export const getRoles = async (): Promise<RoleRef[]> => {
  const response = await apiClient.get<RoleRef[]>('/roles')
  return response.data
}

export const getUsers = async (): Promise<UserWithRoles[]> => {
  const response = await apiClient.get<UserWithRoles[]>('/users')
  return response.data
}

// Everyone the people picker can offer: read whole and searched in the browser.
export const getDirectory = async (): Promise<DirectoryResponse> => {
  const response = await apiClient.get<DirectoryResponse>('/users/directory')
  return response.data
}

// Add a person picked from Entra and grant roles in one step. The server reads
// their UPN and name from Entra itself; only the object id is sent.
export const createUser = async (
  entraObjectId: string,
  roleIds: number[]
): Promise<UserWithRoles> => {
  const response = await apiClient.post<UserWithRoles>('/users', {
    entra_object_id: entraObjectId,
    role_ids: roleIds,
  })
  return response.data
}

// Remove a user from BreadGPT (strips their BreadGPT roles and deactivates them).
export const deleteUser = async (email: string): Promise<void> => {
  await apiClient.delete(`/users/${encodeURIComponent(email)}`)
}

// A role assignment is the resource /users/{email}/roles/{roleId}:
// PUT makes it exist (idempotent), DELETE removes it.
const roleUrl = (email: string, roleId: number): string =>
  `/users/${encodeURIComponent(email)}/roles/${roleId}`

export const assignRole = async (
  email: string,
  roleId: number
): Promise<void> => {
  await apiClient.put(roleUrl(email, roleId))
}

export const removeRole = async (
  email: string,
  roleId: number
): Promise<void> => {
  await apiClient.delete(roleUrl(email, roleId))
}
